import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import Button from '@alfa-retail-react/components/button';
import { FormProvider } from 'react-hook-form';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    ShowComponent,
    useForm,
    useNavigate,
} from '@alfa-retail-react/components';

import { ConfirmationSkeleton } from 'app/component/page/skeletons';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { unauthorizedExceptionHandlerOnLoadData } from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { networkExceptionHandlerOnLoadData } from 'app/logic/pages/error/networkExceptionHandler';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { TOTAL_STEP_COUNT } from 'app/constants/step';
import { Step } from 'app/component/step/Step';
import { ConfirmationPageStore } from 'app/logic/pages/confirmation/ConfirmationPageStore';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { ProductDetailsFormSection } from 'app/logic/pages/confirmation/ProductDetailsFormSection';
import { DeliveryAddressSection } from 'app/logic/pages/confirmation/DeliveryAddressSection';
import { DocumentsFormSection } from 'app/logic/pages/confirmation/DocumentsFormSection';
import { useAnalytics } from 'app/analytics/useAnalytics';

import 'app/logic/pages/confirmation/ConfirmationPage.less';

const PAGE_NAME = 'ConfirmationPage';

export type ConfirmationFormValues = {
    conditionAgreement: boolean;
    documents: Array<{ documentTouched: boolean }>;
};

export const ConfirmationPage = observer(() => {
    const navigate = useNavigate();
    const applicationContext = useApplicationContext();
    const analytics = useAnalytics(
        applicationContext.productCode,
        'ConfirmationDocumentPage',
        false,
    );
    const pageStore = useRef(new ConfirmationPageStore()).current;
    const notification = useNotificationContext();

    const { loadStateStore } = pageStore;

    const form = useForm<ConfirmationFormValues>({});
    const { handleSubmitWithValidationError } = form;

    useEffect(() => {
        analytics.pageDisplayed();

        pageStore
            .initialize(sessionStorage.getItem('orderId'))
            .catch(exceptionLogger(PAGE_NAME, 'pageStore.initialize'))
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(defaultExceptionHandler(notification, analytics));
    }, []);

    const backAction = () => {
        analytics.back();
        navigate(-1);
    };

    const onSubmit = async () => {
        analytics.confirm();
        navigate(`/${applicationContext.productCode}/sms`);
    };

    const isLoading =
        pageStore.actionStateStore.isLoading ||
        pageStore.downloadAllStateStore.isLoading;

    return (
        <OverContentLoadableBlock show={isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                canGoPreviousPage
                backAction={backAction}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>
                        <div className={'confirmationPage'}>
                            <ShowComponent
                                condition={loadStateStore.isLoaded}
                                alternative={<ConfirmationSkeleton />}
                            >
                                <>
                                    <Step
                                        current={TOTAL_STEP_COUNT}
                                        total={TOTAL_STEP_COUNT}
                                    />

                                    <ProductDetailsFormSection
                                        pageStore={pageStore}
                                    />
                                    <DeliveryAddressSection
                                        pageStore={pageStore}
                                    />

                                    <FormProvider<ConfirmationFormValues>
                                        {...form}
                                    >
                                        <form
                                            onSubmit={handleSubmitWithValidationError(
                                                {
                                                    onValid: onSubmit,
                                                },
                                            )}
                                        >
                                            <DocumentsFormSection
                                                pageStore={pageStore}
                                                unauthorizedHandler={unauthorizedExceptionHandlerOnLoadData(
                                                    {
                                                        navigate,
                                                        phoneNumber:
                                                            applicationContext.phoneNumber,
                                                    },
                                                )}
                                            />
                                            <Button
                                                className="submitAction"
                                                view="accent"
                                                type="submit"
                                                size="m"
                                                disabled={isLoading}
                                            >
                                                Подтвердить
                                            </Button>
                                        </form>
                                    </FormProvider>
                                </>
                            </ShowComponent>
                        </div>
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
